import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import {
  DEFAULT_BUILDING_HEIGHT,
  WIN_FLOOR_HEIGHT, WIN_HEIGHT, WIN_SPACING, WIN_DAY_COLOR,
} from '../constants.js';

// ── Default colours by building type ─────────────────────────────────────────
// [wallHex, roofHex]
const TYPE_COLORS = {
  apartments:    [0xd4c5a9, 0x9c7a5a],
  residential:   [0xd4c5a9, 0x9c7a5a],
  house:         [0xddd0b8, 0xa07850],
  detached:      [0xddd0b8, 0xa07850],
  terrace:       [0xd4c5a9, 0x9c7a5a],
  commercial:    [0xbcccdc, 0x6688aa],
  retail:        [0xc8d4e0, 0x7799bb],
  office:        [0xb0c4d8, 0x5577aa],
  industrial:    [0xb8bec4, 0x7a8088],
  warehouse:     [0xc0c4c8, 0x808488],
  church:        [0xe0d8c8, 0x887060],
  cathedral:     [0xe0d8c8, 0x887060],
  chapel:        [0xe0d8c8, 0x887060],
  school:        [0xd8d0b8, 0x907858],
  university:    [0xd8d0b8, 0x907858],
  hospital:      [0xe8e8e8, 0xaaaaaa],
  hotel:         [0xd0c8b8, 0x886644],
  garage:        [0xb8bec4, 0x7a8088],
  parking:       [0xb4b8bc, 0x787c80],
  yes:           [0xd4c5a9, 0x9c7a5a],
  default:       [0xd4c5a9, 0x9c7a5a],
};

// ── Material colours by building:material OSM tag ────────────────────────────
const MAT_COLORS = {
  brick:     [0xcc8855, 0x994433],
  stone:     [0xb8b0a0, 0x887860],
  concrete:  [0xb0b4b8, 0x888c90],
  glass:     [0xaaccee, 0x6699bb],
  metal:     [0xb0b8c0, 0x787e84],
  wood:      [0xc8a870, 0x906040],
  plaster:   [0xe0d8cc, 0xa89878],
};

// ── Roof height as fraction of building height (default when roof:height absent) ─
const ROOF_HEIGHT_FRACTION = {
  pyramidal: 0.55,
  hipped:    0.35,
  pitched:   0.40,
  gabled:    0.40,
  skillion:  0.22,
  dome:      0.65,
  onion:     0.85,
};

// Cached window colour
const _winColor = new THREE.Color(WIN_DAY_COLOR);

/**
 * Build building meshes from parsed OSM data.
 * Returns a THREE.Group containing wall and roof meshes.
 *
 * @param {Array}    buildings  Parsed building objects
 * @param {Object}   proj       Projection helper
 * @param {Function} [elevFn]   Optional: elevFn(x, z) → metres above scene centre
 */
export function buildBuildingMesh(buildings, proj, elevFn = null) {
  const wallGeos = [];
  const roofGeos = [];

  for (const bldg of buildings) {
    try {
      const result = buildSingle(bldg, proj, elevFn);
      if (result.walls) wallGeos.push(result.walls);
      if (result.roof)  roofGeos.push(result.roof);
    } catch {
      // skip degenerate buildings
    }
  }

  const group = new THREE.Group();
  const wallMat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    side: THREE.DoubleSide,
    roughness: 0.85,
    metalness: 0.0,
  });
  const roofMat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    side: THREE.DoubleSide,
    roughness: 0.75,
    metalness: 0.05,
  });

  if (wallGeos.length > 0) {
    const merged = mergeGeometries(wallGeos, false);
    wallGeos.forEach(g => g.dispose());
    const mesh = new THREE.Mesh(merged, wallMat);
    mesh.castShadow = mesh.receiveShadow = true;
    group.add(mesh);
  }

  if (roofGeos.length > 0) {
    const merged = mergeGeometries(roofGeos, false);
    roofGeos.forEach(g => g.dispose());
    const mesh = new THREE.Mesh(merged, roofMat);
    mesh.castShadow = mesh.receiveShadow = true;
    group.add(mesh);
  }

  return group;
}

// ── Single building ───────────────────────────────────────────────────────────

function buildSingle(bldg, proj, elevFn) {
  const height    = bldg.height || DEFAULT_BUILDING_HEIGHT;
  const roofShape = bldg.roofShape || 'flat';

  const [defaultWall, defaultRoof] = resolveColors(bldg);
  const wCol = parseColor(bldg.wallColor, defaultWall);
  const rCol = parseColor(bldg.roofColor, defaultRoof);

  const outer = projectRing(bldg.nodes, proj);
  if (outer.length < 3) return {};

  // Ground elevation at building centroid
  let groundY = 0;
  if (elevFn) {
    let sx = 0, sz = 0;
    for (const p of outer) { sx += p.x; sz += p.z; }
    groundY = elevFn(sx / outer.length, sz / outer.length);
  }

  // Roof height: explicit tag > proportional default > minimum 1.5 m
  const fracH  = ROOF_HEIGHT_FRACTION[roofShape] ?? 0;
  const roofH  = bldg.roofHeight
    ? bldg.roofHeight
    : Math.max(1.5, height * fracH * 0.65);

  // ── Walls ─────────────────────────────────────────────────────────────────
  const rings = [
    outer,
    ...(bldg.holes || [])
      .map(h => projectRing(h, proj))
      .filter(r => r.length >= 3),
  ];

  const wallPos = [];
  const wallNrm = [];
  const wallCol = [];
  const wallIdx = [];

  for (const ring of rings) {
    const n = ring.length;
    for (let i = 0; i < n; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % n];

      // Edge direction and outward normal (in XZ plane)
      const ex = b.x - a.x, ez = b.z - a.z;
      const len = Math.sqrt(ex * ex + ez * ez) || 1;
      const nx = ez / len, nz = -ex / len;

      const base = wallPos.length / 3;
      wallPos.push(
        a.x, groundY,          a.z,
        b.x, groundY,          b.z,
        b.x, groundY + height, b.z,
        a.x, groundY + height, a.z,
      );
      for (let v = 0; v < 4; v++) {
        wallNrm.push(nx, 0, nz);
        wallCol.push(wCol.r, wCol.g, wCol.b);
      }
      wallIdx.push(base, base + 1, base + 2,  base, base + 2, base + 3);

      // Windows on this wall face
      _addWindows(len, height, groundY, a, b, nx, nz, wallPos, wallNrm, wallCol, wallIdx);
    }
  }

  const wallGeo = new THREE.BufferGeometry();
  wallGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(wallPos), 3));
  wallGeo.setAttribute('normal',   new THREE.BufferAttribute(new Float32Array(wallNrm), 3));
  wallGeo.setAttribute('color',    new THREE.BufferAttribute(new Float32Array(wallCol), 3));
  wallGeo.setIndex(wallIdx);

  // ── Roof ──────────────────────────────────────────────────────────────────
  const roofGeo = _buildRoof(roofShape, outer, bldg.holes, groundY, height, roofH, rCol, proj);

  return { walls: wallGeo, roof: roofGeo };
}

// ── Windows ───────────────────────────────────────────────────────────────────

function _addWindows(wallW, height, groundY, a, b, nx, nz, wallPos, wallNrm, wallCol, wallIdx) {
  // Skip tiny walls or very short buildings
  if (wallW < WIN_SPACING * 0.8 || height < WIN_FLOOR_HEIGHT * 1.6) return;

  const nFloors = Math.max(0, Math.floor(height / WIN_FLOOR_HEIGHT) - 1);
  const nCols   = Math.max(1, Math.floor(wallW / WIN_SPACING));
  if (nFloors === 0) return;

  const du    = 1.0 / nCols;
  const SILL  = 0.55;   // floor-to-sill distance
  const BUMP  = 0.06;   // push window quad in front of wall

  for (let f = 0; f < nFloors; f++) {
    const yBot = groundY + (f + 1) * WIN_FLOOR_HEIGHT + SILL;
    const yTop = yBot + WIN_HEIGHT;
    if (yTop > groundY + height - 0.25) continue;

    for (let c = 0; c < nCols; c++) {
      const u0 = (c + 0.18) * du;
      const u1 = (c + 0.82) * du;

      const ax = a.x + u0 * (b.x - a.x);
      const az = a.z + u0 * (b.z - a.z);
      const bx = a.x + u1 * (b.x - a.x);
      const bz = a.z + u1 * (b.z - a.z);

      const base = wallPos.length / 3;
      wallPos.push(
        ax + nx * BUMP, yBot, az + nz * BUMP,
        bx + nx * BUMP, yBot, bz + nz * BUMP,
        bx + nx * BUMP, yTop, bz + nz * BUMP,
        ax + nx * BUMP, yTop, az + nz * BUMP,
      );
      for (let v = 0; v < 4; v++) {
        wallNrm.push(nx, 0, nz);
        wallCol.push(_winColor.r, _winColor.g, _winColor.b);
      }
      wallIdx.push(base, base + 1, base + 2,  base, base + 2, base + 3);
    }
  }
}

// ── Roof dispatch ─────────────────────────────────────────────────────────────

function _buildRoof(shape, outer, holes, groundY, height, roofH, rCol, proj) {
  switch (shape) {
    case 'pyramidal':  return _roofPyramidal(outer, groundY, height, roofH, rCol);
    case 'pitched':
    case 'gabled':     return _roofPitched(outer, groundY, height, roofH, rCol);
    case 'hipped':     return _roofHipped(outer, groundY, height, roofH, rCol);
    case 'skillion':   return _roofSkillion(outer, groundY, height, roofH, rCol);
    case 'dome':       return _roofDome(outer, groundY, height, roofH, rCol, false);
    case 'onion':      return _roofDome(outer, groundY, height, roofH, rCol, true);
    default:           return _roofFlat(outer, holes, groundY, height, rCol, proj);
  }
}

// ── Flat roof ─────────────────────────────────────────────────────────────────

function _roofFlat(outer, holes, groundY, height, rCol, proj) {
  const shape = new THREE.Shape(outer.map(p => new THREE.Vector2(p.x, -p.z)));

  for (const holeNodes of (holes || [])) {
    const hr = projectRing(holeNodes, proj);
    if (hr.length < 3) continue;
    shape.holes.push(new THREE.Path(hr.map(p => new THREE.Vector2(p.x, -p.z))));
  }

  const geo = new THREE.ShapeGeometry(shape);
  geo.rotateX(-Math.PI / 2);
  geo.deleteAttribute('uv'); // ShapeGeometry adds uv; remove to match manual roof geos
  geo.translate(0, groundY + height, 0);
  _colorGeo(geo, rCol);
  return geo;
}

// ── Pyramidal roof ────────────────────────────────────────────────────────────

function _roofPyramidal(outer, groundY, height, roofH, rCol) {
  const cx   = outer.reduce((s, p) => s + p.x, 0) / outer.length;
  const cz   = outer.reduce((s, p) => s + p.z, 0) / outer.length;
  const baseY = groundY + height;
  const apexY = baseY + roofH;

  const pos = [], nrm = [], col = [], idx = [];

  for (let i = 0; i < outer.length; i++) {
    const a = outer[i];
    const b = outer[(i + 1) % outer.length];
    const base = pos.length / 3;

    pos.push(a.x, baseY, a.z,  b.x, baseY, b.z,  cx, apexY, cz);

    // Face normal via cross product
    const ux = b.x - a.x, uy = 0, uz = b.z - a.z;
    const vx = cx - a.x, vy = apexY - baseY, vz = cz - a.z;
    let fnx = uy * vz - uz * vy;
    let fny = uz * vx - ux * vz;
    let fnz = ux * vy - uy * vx;
    const fLen = Math.sqrt(fnx * fnx + fny * fny + fnz * fnz) || 1;
    fnx /= fLen; fny /= fLen; fnz /= fLen;

    for (let j = 0; j < 3; j++) {
      nrm.push(fnx, fny, fnz);
      col.push(rCol.r, rCol.g, rCol.b);
    }
    idx.push(base, base + 1, base + 2);
  }

  return _makeGeo(pos, nrm, col, idx);
}

// ── Pitched (gabled) roof ─────────────────────────────────────────────────────

function _roofPitched(outer, groundY, height, roofH, rCol) {
  return _tentRoof(outer, groundY, height, roofH, rCol, false);
}

// ── Hipped roof ───────────────────────────────────────────────────────────────

function _roofHipped(outer, groundY, height, roofH, rCol) {
  return _tentRoof(outer, groundY, height, roofH, rCol, true);
}

/**
 * Common implementation for pitched and hipped roofs.
 * Uses ShapeGeometry with per-vertex Y modification.
 * @param {boolean} hipped  If true, also slopes the gable ends (hip).
 */
function _tentRoof(outer, groundY, height, roofH, rCol, hipped) {
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const p of outer) {
    if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z; if (p.z > maxZ) maxZ = p.z;
  }

  const spanX  = maxX - minX;
  const spanZ  = maxZ - minZ;
  const cx     = (minX + maxX) / 2;
  const cz     = (minZ + maxZ) / 2;
  const baseY  = groundY + height;

  const shape = new THREE.Shape(outer.map(p => new THREE.Vector2(p.x, -p.z)));
  const geo   = new THREE.ShapeGeometry(shape);
  geo.rotateX(-Math.PI / 2);
  geo.deleteAttribute('uv'); // remove ShapeGeometry's auto-uv to keep attributes uniform
  // After rotateX: positions are (scene.x, 0, scene.z)

  const pos = geo.attributes.position;
  const ridgeAlongX = spanX >= spanZ;
  const halfShort   = (ridgeAlongX ? spanZ : spanX) / 2;
  const ridgeHalfLen = hipped
    ? Math.max(0, (ridgeAlongX ? spanX : spanZ) / 2 - halfShort)
    : Infinity;

  for (let i = 0; i < pos.count; i++) {
    const vx = pos.getX(i);
    const vz = pos.getZ(i);

    let t;
    if (ridgeAlongX) {
      const perpDist = halfShort > 0 ? Math.min(1, Math.abs(vz - cz) / halfShort) : 1;
      if (hipped) {
        const endDist = ridgeHalfLen > 0
          ? Math.min(1, Math.max(0, Math.abs(vx - cx) - ridgeHalfLen) / halfShort)
          : 0;
        t = Math.max(perpDist, endDist);
      } else {
        t = perpDist;
      }
    } else {
      const perpDist = halfShort > 0 ? Math.min(1, Math.abs(vx - cx) / halfShort) : 1;
      if (hipped) {
        const endDist = ridgeHalfLen > 0
          ? Math.min(1, Math.max(0, Math.abs(vz - cz) - ridgeHalfLen) / halfShort)
          : 0;
        t = Math.max(perpDist, endDist);
      } else {
        t = perpDist;
      }
    }

    pos.setY(i, baseY + roofH * (1 - t));
  }

  pos.needsUpdate = true;
  geo.computeVertexNormals();
  _colorGeo(geo, rCol);
  return geo;
}

// ── Skillion roof ─────────────────────────────────────────────────────────────

function _roofSkillion(outer, groundY, height, roofH, rCol) {
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const p of outer) {
    if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z; if (p.z > maxZ) maxZ = p.z;
  }
  const spanX = maxX - minX;
  const spanZ = maxZ - minZ;
  const baseY = groundY + height;

  const shape = new THREE.Shape(outer.map(p => new THREE.Vector2(p.x, -p.z)));
  const geo   = new THREE.ShapeGeometry(shape);
  geo.rotateX(-Math.PI / 2);
  geo.deleteAttribute('uv'); // remove ShapeGeometry's auto-uv to keep attributes uniform

  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const vx = pos.getX(i);
    const vz = pos.getZ(i);
    const t = spanX >= spanZ
      ? (spanX > 0 ? (vx - minX) / spanX : 0)
      : (spanZ > 0 ? (vz - minZ) / spanZ : 0);
    pos.setY(i, baseY + roofH * t);
  }

  pos.needsUpdate = true;
  geo.computeVertexNormals();
  _colorGeo(geo, rCol);
  return geo;
}

// ── Dome / Onion roof ─────────────────────────────────────────────────────────

function _roofDome(outer, groundY, height, roofH, rCol, isOnion) {
  let cx = 0, cz = 0, maxR = 0;
  for (const p of outer) { cx += p.x; cz += p.z; }
  cx /= outer.length; cz /= outer.length;
  for (const p of outer) {
    maxR = Math.max(maxR, Math.sqrt((p.x - cx) ** 2 + (p.z - cz) ** 2));
  }
  maxR = Math.max(maxR, 1);

  const baseY    = groundY + height;
  const SEGS     = 12;
  const RINGS    = 7;
  const pos = [], nrm = [], col = [], idx = [];

  for (let r = 0; r < RINGS; r++) {
    const phi0 = (r / RINGS) * (Math.PI / 2);
    const phi1 = ((r + 1) / RINGS) * (Math.PI / 2);

    for (let s = 0; s < SEGS; s++) {
      const th0 = (s / SEGS) * Math.PI * 2;
      const th1 = ((s + 1) / SEGS) * Math.PI * 2;

      const v = [
        [phi0, th0], [phi0, th1], [phi1, th1], [phi1, th0],
      ];
      const base = pos.length / 3;
      for (const [phi, th] of v) {
        const cp = Math.cos(phi), sp = Math.sin(phi);
        const ct = Math.cos(th),  st = Math.sin(th);
        const x  = cx + maxR * cp * ct;
        const z  = cz + maxR * cp * st;
        const bulge = isOnion ? Math.sin(phi * 2.2) * 0.35 : 0;
        const y  = baseY + roofH * sp * (1 + bulge);
        pos.push(x, y, z);
        nrm.push(cp * ct, sp, cp * st);
        col.push(rCol.r, rCol.g, rCol.b);
      }
      idx.push(base, base + 1, base + 2,  base, base + 2, base + 3);
    }
  }

  return _makeGeo(pos, nrm, col, idx);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function _makeGeo(pos, nrm, col, idx) {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
  geo.setAttribute('normal',   new THREE.BufferAttribute(new Float32Array(nrm), 3));
  geo.setAttribute('color',    new THREE.BufferAttribute(new Float32Array(col), 3));
  geo.setIndex(idx);
  return geo;
}

function _colorGeo(geo, color) {
  const count  = geo.attributes.position.count;
  const colArr = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    colArr[i * 3]     = color.r;
    colArr[i * 3 + 1] = color.g;
    colArr[i * 3 + 2] = color.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colArr, 3));
}

function resolveColors(bldg) {
  if (bldg.buildingMat) {
    const mc = MAT_COLORS[bldg.buildingMat.toLowerCase()];
    if (mc) return mc;
  }
  const type = (bldg.buildingType || 'default').toLowerCase();
  return TYPE_COLORS[type] || TYPE_COLORS.default;
}

function parseColor(osmStr, defaultHex) {
  if (osmStr) {
    try {
      return new THREE.Color(osmStr);
    } catch { /* fall through */ }
  }
  return new THREE.Color(defaultHex);
}

function projectRing(nodes, proj) {
  const result = [];
  for (const n of nodes) {
    if (n.lat === undefined) continue;
    const p = proj.project(n.lat, n.lon);
    if (!isFinite(p.x) || !isFinite(p.z)) continue;
    result.push(p);
  }
  if (result.length >= 2) {
    const f = result[0], l = result[result.length - 1];
    if ((l.x - f.x) ** 2 + (l.z - f.z) ** 2 < 0.0001) result.pop();
  }
  return result;
}
